import { spawnSync } from 'node:child_process';
import fs from 'node:fs';
import os from 'node:os';
import path from 'node:path';

export function pickNodeBinary(): { bin: string; version?: number } {
  const candidates = collectNodeCandidates();

  // Prefer the first viable candidate with version >=18; otherwise pick highest version found.
  let best: { bin: string; version?: number } | undefined;
  let fallback: { bin: string; version?: number } | undefined;

  for (const bin of candidates) {
    const version = getNodeMajor(bin);
    if (!version) continue;
    const current = { bin, version };
    if (version >= 18 && !fallback) {
      fallback = current;
    }
    if (!best || version > (best.version ?? 0)) {
      best = current;
    }
  }

  if (fallback) return fallback;
  if (best) return best;

  // Last resort
  return { bin: candidates[0] ?? 'node' };
}

function collectNodeCandidates(): string[] {
  const seen = new Set<string>();
  const push = (value?: string) => {
    if (!value) return;
    if (seen.has(value)) return;
    seen.add(value);
  };

  const isBun = path.basename(process.execPath).includes('bun');

  push(process.env.CANARY_NODE_BIN);
  push(isBun ? undefined : process.execPath);
  push('node'); // default PATH lookup

  // Collect from `which -a node` (Unix) or `where node` (Windows).
  try {
    const isWin = process.platform === 'win32';
    const result = isWin
      ? spawnSync('where', ['node'], { encoding: 'utf-8', shell: true, windowsHide: true })
      : spawnSync('which', ['-a', 'node'], { encoding: 'utf-8' });
    result.stdout
      ?.toString()
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter((line) => line.length > 0)
      .forEach((line) => push(line));
  } catch {
    // ignore
  }

  // NVM installations (grab highest versions). nvm-for-Windows has a different
  // layout — skip on win32; the PATH scan above covers it.
  if (process.platform !== 'win32') {
    const nvmDir = process.env.NVM_DIR || path.join(os.homedir(), '.nvm');
    const versionsDir = path.join(nvmDir, 'versions', 'node');
    if (fs.existsSync(versionsDir)) {
      try {
        const versions = fs.readdirSync(versionsDir);
        versions
          .sort((a, b) => (a > b ? -1 : 1))
          .forEach((v) => push(path.join(versionsDir, v, 'bin', 'node')));
      } catch {
        // ignore
      }
    }
  }

  return Array.from(seen);
}

function getNodeMajor(bin: string): number | undefined {
  try {
    const result = spawnSync(bin, ['-v'], { encoding: 'utf-8' });
    const output = (result.stdout || result.stderr || '').toString().trim();
    const match = output.match(/^v(\d+)/);
    if (match) return Number(match[1]);
  } catch {
    // ignore
  }
  return undefined;
}
